import React from "react";
import { Helmet } from 'react-helmet-async';
import useNativeSearch from './useNativeSearch';
import RenderItem from './RenderItem';
import RefinementCheckboxList from './RefinementCheckboxList';
import Pagination from './Pagination';
import '../../sass/search.scss';

const PriceFacet = ({ items, selected, onChange }) => {
    const hasSelection = selected.min != null || selected.max != null;
    return (
        <ul className="ais-NumericMenu-list">
            <li
                className={
                    "ais-NumericMenu-item" +
                    (!hasSelection ? " ais-NumericMenu-item--selected" : "")
                }
            >
                <label className="ais-NumericMenu-label">
                    <input
                        type="radio"
                        name="price"
                        className="ais-NumericMenu-radio"
                        checked={!hasSelection}
                        onChange={() => onChange(null, null)}
                    />
                    <span className="ais-NumericMenu-labelText">All prices</span>
                </label>
            </li>
            {items?.map((b) => {
                const active =
                    (selected.min ?? null) === (b.min ?? null) &&
                    (selected.max ?? null) === (b.max ?? null);
                return (
                    <li
                        key={b.label}
                        className={
                            "ais-NumericMenu-item" +
                            (active ? " ais-NumericMenu-item--selected" : "")
                        }
                    >
                        <label className="ais-NumericMenu-label">
                            <input
                                type="radio"
                                name="price"
                                className="ais-NumericMenu-radio"
                                checked={active}
                                onChange={() => onChange(b.min, b.max)}
                            />
                            <span className="ais-NumericMenu-labelText">{b.label}</span>
                        </label>
                    </li>
                );
            })}
        </ul>
    );
};

const Panel = ({ header, children }) => (
    <div className="ais-Panel">
        <div className="ais-Panel-header">{header}</div>
        <div className="ais-Panel-body">{children}</div>
    </div>
);

const SearchLarge = () => {
    const {
        state, data, loading,
        setQuery, toggleFilter, setPrice, setSort, setPage, setFacetQuery, clearAll,
    } = useNativeSearch();

    const searchInputRef = React.useRef(null);
    React.useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    const total = data?.total ?? 0;
    const query = data?.query ?? '';

    return (
        <>
            <Helmet>
                <title>Search Dental Products Online | DentalStores.in</title>
                <meta name="description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="keywords" content="dental product search, buy dental instruments, best dental supplies, dental materials online, dental e-commerce" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://www.dentalstores.in/search" />

                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.dentalstores.in/search" />
                <meta property="og:title" content="Search Dental Products Online | DentalStores.in" />
                <meta property="og:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta property="og:image" content="https://www.dentalstores.in/logo.svg" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Search Dental Products Online | DentalStores.in" />
                <meta name="twitter:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="twitter:image" content="https://www.dentalstores.in/logo.svg" />
            </Helmet>
            <div className="search-container">
                <div className="search-input ais-SearchBox">
                    <input
                        ref={searchInputRef}
                        className="ais-SearchBox-input"
                        type="search"
                        value={state.q}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search for products, brands..."
                    />
                </div>

                <div className="first-row">
                    <div className="results-info">
                        {query ? (
                            <p className="results-info-count">Showing <b>{total}</b> results for <b>"{query}"</b></p>
                        ) : (
                            <p></p>
                        )}
                    </div>

                    <div className="sort-by-wrapper">
                        <p className="sort-by-text">Sort By Options:</p>
                        <select
                            className="ais-SortBy-select"
                            value={state.sort}
                            onChange={(e) => setSort(e.target.value)}
                        >
                            <option value="relevance">Relevance</option>
                            <option value="price_asc">Price: Low to High</option>
                            <option value="price_desc">Price: High to Low</option>
                        </select>
                    </div>
                </div>

                <div className='product_wrapper'>
                    <div className="filters-container">
                        <Panel header="Status">
                            <RefinementCheckboxList
                                items={data?.facets?.stock_status ?? []}
                                selected={state.filters.stock_status}
                                onToggle={(v) => toggleFilter('stock_status', v)}
                            />
                        </Panel>

                        <Panel header="Categories">
                            <input
                                type="text"
                                className="ais-SearchBox-input"
                                placeholder="Search categories..."
                                onChange={(e) => setFacetQuery('categories', e.target.value)}
                            />
                            <RefinementCheckboxList
                                items={data?.facets?.categories ?? []}
                                selected={state.filters.categories}
                                onToggle={(v) => toggleFilter('categories', v)}
                                limit={10}
                            />
                        </Panel>

                        <Panel header="Brands">
                            <input
                                type="text"
                                className="ais-SearchBox-input"
                                placeholder="Search brands..."
                                onChange={(e) => setFacetQuery('brand', e.target.value)}
                            />
                            <RefinementCheckboxList
                                items={data?.facets?.brand ?? []}
                                selected={state.filters.brand}
                                onToggle={(v) => toggleFilter('brand', v)}
                                limit={5}
                            />
                        </Panel>

                        <Panel header="Price Range">
                            <div className="custom-price-range">
                                <PriceFacet
                                    items={data?.facets?.price ?? []}
                                    selected={state.filters.price}
                                    onChange={setPrice}
                                />
                            </div>
                        </Panel>
                    </div>

                    <div className="products-area" style={{ flex: 1, minWidth: 0 }}>
                        {loading && <div className="search-loading">Loading…</div>}
                        {!loading && data?.hits?.length === 0 ? (
                            <h6>Sorry, we couldn't find any results.</h6>
                        ) : (
                            <div className="products-grid">
                                {data?.hits?.map((hit) => (
                                    <RenderItem key={hit.objectID} hit={hit} />
                                ))}
                            </div>
                        )}
                        <Pagination
                            page={state.page}
                            totalPages={
                                data?.total_pages ??
                                (data?.total ? Math.ceil(data.total / state.per_page) : 1)
                            }
                            onChange={setPage}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchLarge;
